import {
  useAddresses,
  useEstimateInternational,
  useServiceItems,
  useStoredOrderItems,
  type LogisticsRoute,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { useAuthToken } from "../lib/auth";

// ─── helpers ────────────────────────────────────────────────────────────────

/** Map shippingType → emoji (mirrors web route-line.tsx). */
function shippingIcon(shippingType: string | null): string {
  switch (shippingType) {
    case "sea":
      return "🚢";
    case "land":
      return "🚆";
    default:
      return "✈";
  }
}

/** Gradient background for the icon tile — index-based so each card looks distinct. */
function iconGradient(index: number): string {
  const gs = [
    "linear-gradient(135deg,#FFE5DD,#FFD3C2)",
    "linear-gradient(135deg,#FFE0E9,#FFC8D6)",
    "linear-gradient(135deg,#FFF8DA,#FFE5A0)",
    "linear-gradient(135deg,#E0F7F1,#B8E8DD)",
    "linear-gradient(135deg,#E8E0FF,#D0C2F5)",
  ];
  // gs is non-empty; index % gs.length is always in-bounds
  return (gs[index % gs.length] as string);
}

interface SortResult {
  sorted: LogisticsRoute[];
  cheapestId: string | null;
  fastestId: string | null;
}

function sortRoutes(routes: LogisticsRoute[]): SortResult {
  const sorted = [...routes].sort((a, b) => {
    if (a.recommended !== b.recommended) return a.recommended ? -1 : 1;
    if (a.carriable !== b.carriable) return a.carriable ? -1 : 1;
    return (a.totalCostNumber ?? Infinity) - (b.totalCostNumber ?? Infinity);
  });

  let cheapestId: string | null = null;
  let minCost = Infinity;
  let fastestId: string | null = null;
  let minDays = Infinity;

  for (const r of routes) {
    if (!r.carriable) continue;
    if (r.totalCostNumber != null && r.totalCostNumber < minCost) {
      minCost = r.totalCostNumber;
      cheapestId = r.routeId;
    }
    if (r.minDeliveredDays != null && r.minDeliveredDays < minDays) {
      minDays = r.minDeliveredDays;
      fastestId = r.routeId;
    }
  }

  return { sorted, cheapestId, fastestId };
}

// ─── page ───────────────────────────────────────────────────────────────────

export function RehearsalPage() {
  const { t } = useTranslation("rehearsal");
  const authToken = useAuthToken();

  const ready = useStoredOrderItems(authToken, { isParcelable: "Y", pageNo: 1, pageSize: 30 });
  const items = ready.data?.records ?? [];
  const itemCount = items.length;

  const { data: addresses } = useAddresses(authToken);
  const defaultAddr = useMemo(() => {
    const list = Array.isArray(addresses) ? addresses : [];
    return list.find((a) => a.isDefault) ?? list[0] ?? undefined;
  }, [addresses]);

  const { data: services } = useServiceItems();

  const estimate = useEstimateInternational();
  const [weightKg, setWeightKg] = useState("1.85");
  const [len, setLen] = useState("");
  const [wid, setWid] = useState("");
  const [hei, setHei] = useState("");
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [activeServiceIds, setActiveServiceIds] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);

  const routes = estimate.data ?? [];
  const { sorted, cheapestId, fastestId } = useMemo(() => sortRoutes(routes), [routes]);
  const selectedRoute = sorted.find((r) => r.routeId === selectedRouteId) ?? null;

  // Weight calculations
  const actualG = Math.round(Number(weightKg) * 1000) || 0;
  const volG =
    len && wid && hei
      ? Math.round(((Number(len) * Number(wid) * Number(hei)) / 6000) * 1000)
      : null;
  const chargeableG = volG != null ? Math.max(volG, actualG) : actualG;

  // Service toggle
  const activeServices = (services ?? []).filter((s) => activeServiceIds.has(s.id));
  const serviceFeeMinor = activeServices.reduce((sum, s) => sum + s.priceMinor, 0);

  // Receipt math
  const freightNumber = selectedRoute?.cargoCostNumber ?? selectedRoute?.totalCostNumber ?? null;
  const freightDisplay = selectedRoute?.cargoCostDisplay ?? selectedRoute?.totalCostDisplay ?? null;
  const freightMinor = freightNumber != null ? Math.round(freightNumber * 100) : null;
  const grandTotalMinor = freightMinor != null ? freightMinor + serviceFeeMinor : null;

  const handleEstimate = () => {
    setFormError(null);
    if (!defaultAddr?.countryCode) {
      setFormError(t("err.no_address"));
      return;
    }
    const weightGrams = Math.round(Number(weightKg) * 1000);
    if (!Number.isFinite(weightGrams) || weightGrams <= 0) {
      setFormError(t("err.weight"));
      return;
    }
    setSelectedRouteId(null);
    estimate.mutate(
      {
        country: defaultAddr.countryCode,
        weightGrams,
        lengthCm: len ? Number(len) : null,
        widthCm: wid ? Number(wid) : null,
        heightCm: hei ? Number(hei) : null,
      },
      {
        onSuccess: (rows) => {
          const { sorted: s } = sortRoutes(rows);
          const first = s.find((r) => r.carriable);
          if (first) setSelectedRouteId(first.routeId);
        },
      },
    );
  };

  const toggleService = (id: string) => {
    setActiveServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("head.title")} back />

      {/* Step indicator */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 0,
          padding: "12px 16px",
          background: "#fff",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <StepCircle done>✓</StepCircle>
        <StepLine active />
        <StepCircle active>2</StepCircle>
        <StepLine />
        <StepCircle>3</StepCircle>
        <span
          style={{
            marginLeft: 10,
            fontSize: 11,
            color: "var(--color-text-muted)",
            fontWeight: 700,
          }}
        >
          选 → <strong style={{ color: "var(--color-primary-fill)" }}>预演</strong> → 提交
        </span>
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        {/* Recap: count + weight */}
        <div
          style={{
            background: "var(--color-surface)",
            borderRadius: 10,
            padding: "10px 12px",
            display: "flex",
            gap: 8,
            alignItems: "center",
            fontSize: 11,
          }}
        >
          <strong>
            {itemCount > 0
              ? `${t("recap.count", { count: itemCount })} · ${(chargeableG / 1000).toFixed(2)} kg`
              : t("recap.count", { count: 0 })}
          </strong>
          <Link
            to={"/warehouse" as never}
            style={{
              marginLeft: "auto",
              color: "var(--color-primary-fill)",
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            {t("recap.edit")}
          </Link>
        </div>

        {/* Address (clickable → address page) */}
        {defaultAddr ? (
          <Link
            to={"/address" as never}
            style={{
              background: "linear-gradient(180deg,var(--color-primary-soft),#fff)",
              border: "1.5px solid var(--color-primary-fill)",
              borderRadius: 12,
              padding: "10px 12px",
              display: "flex",
              gap: 8,
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              fontSize: 11,
            }}
          >
            <span style={{ fontSize: 18 }}>📍</span>
            <div style={{ flex: 1 }}>
              <strong>
                {defaultAddr.city ? `${defaultAddr.city} · ` : ""}
                {defaultAddr.recipientName}
              </strong>
              <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>
                {defaultAddr.fullAddress}
              </div>
            </div>
            <span style={{ color: "var(--color-primary-fill)", fontSize: 14 }}>›</span>
          </Link>
        ) : (
          <div
            style={{
              background: "var(--color-surface)",
              borderRadius: 10,
              padding: "10px 12px",
              fontSize: 11,
              textAlign: "center",
              color: "var(--color-text-muted)",
            }}
          >
            {t("dest.none")}{" "}
            <Link
              to={"/address" as never}
              style={{ color: "var(--color-primary-fill)", fontWeight: 700 }}
            >
              {t("dest.add")}
            </Link>
          </div>
        )}

        {/* Weight / dimensions input */}
        <div className="tg-card">
          <h4 className="tg-card__h">{t("pkg.title")}</h4>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 8 }}>
            {t("pkg.subtitle")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <Field
              label={t("pkg.weight")}
              value={weightKg}
              onChange={setWeightKg}
              placeholder="kg"
            />
            <Field label={t("pkg.length")} value={len} onChange={setLen} placeholder="cm" />
            <Field label={t("pkg.width")} value={wid} onChange={setWid} placeholder="cm" />
            <Field label={t("pkg.height")} value={hei} onChange={setHei} placeholder="cm" />
          </div>
          {formError && (
            <div
              role="alert"
              style={{
                marginTop: 8,
                fontSize: 11,
                color: "var(--color-cta)",
                fontWeight: 600,
              }}
            >
              {formError}
            </div>
          )}
          <button
            type="button"
            onClick={handleEstimate}
            disabled={estimate.isPending}
            style={{
              marginTop: 10,
              width: "100%",
              background: "var(--color-primary-fill)",
              color: "#fff",
              border: 0,
              padding: "10px 0",
              borderRadius: 8,
              fontWeight: 800,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            {estimate.isPending ? t("pkg.calculating") : t("pkg.submit")}
          </button>
        </div>

        {/* Route cards */}
        {sorted.length > 0 && (
          <>
            <div className="tg-sec-h">
              <span className="tg-sec-h__t">{t("lines.title", { count: sorted.length })}</span>
            </div>
            {sorted.map((r, i) => {
              const isRec = r.recommended;
              const isCheapest = r.routeId === cheapestId && !isRec;
              const isFastest = r.routeId === fastestId && !isRec;
              const isSelected = r.routeId === selectedRouteId;
              const isCarriable = r.carriable;
              return (
                <button
                  key={r.routeId}
                  type="button"
                  onClick={() => isCarriable && setSelectedRouteId(r.routeId)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    background: isRec
                      ? "linear-gradient(180deg,var(--color-primary-soft),#fff)"
                      : isSelected
                        ? "var(--color-surface)"
                        : "#fff",
                    border: isSelected
                      ? "2px solid var(--color-primary-fill)"
                      : isRec
                        ? "1.5px solid var(--color-primary-fill)"
                        : "1px solid var(--color-border)",
                    borderRadius: 12,
                    padding: isRec ? 14 : 12,
                    position: "relative",
                    cursor: isCarriable ? "pointer" : "default",
                    opacity: isCarriable ? 1 : 0.5,
                    marginBottom: 8,
                  }}
                >
                  {/* Badge */}
                  {isRec && (
                    <span
                      style={{
                        position: "absolute",
                        top: -8,
                        left: 14,
                        padding: "2px 8px",
                        background: "var(--color-sticker-yellow)",
                        color: "var(--color-sticker-yellow-fg)",
                        fontFamily: "var(--font-mono)",
                        fontSize: 9,
                        fontWeight: 800,
                        borderRadius: 4,
                      }}
                    >
                      👑 推荐
                    </span>
                  )}
                  {isCheapest && (
                    <span
                      style={{
                        position: "absolute",
                        top: -8,
                        left: 14,
                        padding: "2px 8px",
                        background: "var(--color-primary-fill)",
                        color: "#fff",
                        fontFamily: "var(--font-mono)",
                        fontSize: 9,
                        fontWeight: 800,
                        borderRadius: 4,
                      }}
                    >
                      💰 最便宜
                    </span>
                  )}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "36px 1fr auto",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    {/* Icon tile */}
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 10,
                        background: iconGradient(i),
                        display: "grid",
                        placeItems: "center",
                        fontSize: 18,
                      }}
                    >
                      {shippingIcon(r.shippingType)}
                    </div>

                    {/* Name + description */}
                    <div>
                      <strong style={{ fontSize: 13 }}>
                        {r.name}
                        {isFastest && (
                          <span
                            style={{
                              marginLeft: 4,
                              fontSize: 8,
                              padding: "1px 4px",
                              background: "var(--color-cta)",
                              color: "#fff",
                              borderRadius: 3,
                              verticalAlign: "middle",
                            }}
                          >
                            最快
                          </span>
                        )}
                      </strong>
                      {r.description && (
                        <div
                          style={{
                            fontSize: 10,
                            color: "var(--color-text-muted)",
                            marginTop: 2,
                          }}
                        >
                          {r.description}
                        </div>
                      )}
                    </div>

                    {/* Price + days */}
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: isRec ? 18 : 16,
                          fontWeight: 900,
                          color: isRec ? "var(--color-primary-fill)" : "inherit",
                        }}
                      >
                        {r.totalCostDisplay ??
                          (r.totalCostNumber != null
                            ? formatPrice(Math.round(r.totalCostNumber * 100), "ru-RU", "RUB")
                            : "—")}
                      </div>
                      {r.minDeliveredDays != null && (
                        <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
                          {r.minDeliveredDays}
                          {r.maxDeliveredDays ? `-${r.maxDeliveredDays}` : ""} 天
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </>
        )}

        {/* Packing services with toggle */}
        {services && services.length > 0 && (
          <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
            <div
              style={{
                padding: "8px 12px",
                background: "var(--color-surface)",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {t("pack.title")}
            </div>
            {services.map((s) => (
              <div
                key={s.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  gap: 10,
                  padding: "10px 12px",
                  alignItems: "center",
                  borderTop: "1px solid var(--color-border)",
                  fontSize: 11,
                }}
              >
                <div>
                  <strong>{s.title}</strong>
                  {s.description && (
                    <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                      {s.description}
                    </div>
                  )}
                  {!s.isFree && (
                    <div
                      style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 1 }}
                    >
                      + {formatPrice(s.priceMinor, "ru-RU", "RUB")}
                      {s.chargeUnit ? ` / ${s.chargeUnit}` : ""}
                    </div>
                  )}
                  {s.isFree && (
                    <div
                      style={{
                        fontSize: 9,
                        color: "var(--color-primary-fill)",
                        fontWeight: 700,
                        marginTop: 1,
                      }}
                    >
                      {t("pack.free")}
                    </div>
                  )}
                </div>
                {/* Toggle switch */}
                <button
                  type="button"
                  aria-pressed={activeServiceIds.has(s.id)}
                  aria-label={s.title}
                  onClick={() => toggleService(s.id)}
                  style={{
                    width: 40,
                    height: 22,
                    borderRadius: 11,
                    border: 0,
                    background: activeServiceIds.has(s.id)
                      ? "var(--color-primary-fill)"
                      : "var(--color-border)",
                    position: "relative",
                    cursor: "pointer",
                    flexShrink: 0,
                    transition: "background 0.18s",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      top: 2,
                      left: activeServiceIds.has(s.id) ? 20 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: "#fff",
                      transition: "left 0.18s",
                      boxShadow: "0 1px 3px rgba(0,0,0,.2)",
                    }}
                  />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Receipt block */}
        <div className="tg-card" style={{ background: "var(--color-surface)" }}>
          <h4 className="tg-card__h" style={{ marginBottom: 10 }}>
            {t("receipt.title")}
          </h4>

          {/* Freight row */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              paddingBottom: 8,
              borderBottom: "1px solid var(--color-border)",
              fontSize: 11,
            }}
          >
            <div>
              <div style={{ fontWeight: 700 }}>{t("receipt.freight")}</div>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                {selectedRoute
                  ? t("receipt.freight_detail", {
                      route: selectedRoute.name,
                      weight: (chargeableG / 1000).toFixed(2),
                    })
                  : t("receipt.pick_line")}
              </div>
            </div>
            <div style={{ fontWeight: 800, whiteSpace: "nowrap", marginLeft: 8 }}>
              {freightDisplay ?? "—"}
            </div>
          </div>

          {/* Active service rows */}
          {activeServices.map((s) => (
            <div
              key={s.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                padding: "8px 0",
                borderBottom: "1px solid var(--color-border)",
                fontSize: 11,
              }}
            >
              <div style={{ fontWeight: 700 }}>{s.title}</div>
              <div
                style={{
                  fontWeight: 800,
                  color: s.isFree ? "var(--color-primary-fill)" : "inherit",
                  whiteSpace: "nowrap",
                  marginLeft: 8,
                }}
              >
                {s.isFree ? t("receipt.free") : formatPrice(s.priceMinor, "ru-RU", "RUB")}
              </div>
            </div>
          ))}

          {/* Insurance row (free if route has it) */}
          {selectedRoute?.hasInsurance && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom: "1px solid var(--color-border)",
                fontSize: 11,
              }}
            >
              <div style={{ fontWeight: 700 }}>{t("receipt.insurance")}</div>
              <div
                style={{
                  fontWeight: 800,
                  color: "var(--color-primary-fill)",
                  marginLeft: 8,
                }}
              >
                {t("receipt.free")}
              </div>
            </div>
          )}

          {/* Grand total */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              paddingTop: 10,
              marginTop: 4,
            }}
          >
            <div>
              <div style={{ fontSize: 11, fontWeight: 800 }}>{t("receipt.total")}</div>
              <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                {t("receipt.total_note")}
              </div>
            </div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 22,
                fontWeight: 900,
                color: "var(--color-cta)",
                letterSpacing: "-0.015em",
                lineHeight: 1,
              }}
            >
              {grandTotalMinor != null ? formatPrice(grandTotalMinor, "ru-RU", "RUB") : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* CTA — only enabled when a route is selected */}
      <TgMain disabled={selectedRoute === null}>{t("receipt.submit")}</TgMain>
    </TgPage>
  );
}

// ─── sub-components ──────────────────────────────────────────────────────────

function StepCircle({
  active,
  done,
  children,
}: {
  active?: boolean;
  done?: boolean;
  children: React.ReactNode;
}) {
  const base = {
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "grid" as const,
    placeItems: "center" as const,
    fontSize: 10,
    fontWeight: 800 as const,
  };
  if (done)
    return (
      <div style={{ ...base, background: "var(--color-primary-fill)", color: "#fff" }}>
        {children}
      </div>
    );
  if (active)
    return (
      <div
        style={{
          ...base,
          width: 24,
          height: 24,
          background: "var(--color-primary-soft)",
          border: "2px solid var(--color-primary-fill)",
          color: "var(--color-primary-fill)",
        }}
      >
        {children}
      </div>
    );
  return (
    <div
      style={{
        ...base,
        background: "var(--color-bg)",
        border: "2px solid var(--color-border)",
        color: "var(--color-text-muted)",
      }}
    >
      {children}
    </div>
  );
}

function StepLine({ active }: { active?: boolean }) {
  return (
    <div
      style={{
        flex: 1,
        height: 2,
        background: active ? "var(--color-primary-fill)" : "var(--color-border)",
        margin: "0 4px",
      }}
    />
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <label
      style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10, fontWeight: 700 }}
    >
      {label}
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: 6,
          padding: "6px 8px",
          fontSize: 12,
          width: "100%",
        }}
      />
    </label>
  );
}
